<template>
  <div class="component-editpricerangepricesdialog">
    <v-dialog v-model="dialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="headline">
          {{ productNameLocalized }}
        </v-card-title>

        <v-card-subtitle v-if="priceRangeName">
          {{ priceRangeName }}
        </v-card-subtitle>

        <v-divider></v-divider>

        <v-card-text>
          <v-alert
            type="info"
            outlined
            class="mb-4 mt-4"
          >
            {{ $t('pricelists.products.editPricesDialog.description') }}
          </v-alert>

          <v-alert
            v-model="deleteError"
            type="error"
            dismissible
            class="mb-4"
          >
            {{ deleteErrorMessage }}
          </v-alert>

          <v-data-table
            v-if="editablePrices.length > 0"
            class="elevation-1 price-table"
            :headers="priceHeaders"
            :items="editablePrices"
            disable-pagination
            hide-default-footer
          >
            <template v-slot:item.packaging="{ item }">
              <v-edit-dialog
                :return-value="item.packaging"
                @save="onPackagingSave(item)"
              >
                <template v-if="item.packaging">
                  {{ formatPackaging(item.packaging) }}
                </template>
                <span v-else class="red--text">
                  {{ $t('pricelists.products.editPricesDialog.missingPackaging') }}
                </span>
                <template v-slot:input>
                  <v-text-field
                    v-model="item.packaging"
                    :label="$t('pricelists.products.editPricesDialog.packagingLabel')"
                    single-line
                    type="text"
                    inputmode="decimal"
                  ></v-text-field>
                </template>
              </v-edit-dialog>
            </template>

            <template v-slot:item.price="{ item }">
              <v-edit-dialog
                :return-value="item.price"
                @save="onPriceSave(item)"
              >
                <template v-if="item.price">
                  {{ formatPrice(item.price) }}
                </template>
                <span v-else class="red--text">
                  {{ $t('pricelists.products.editPricesDialog.missingPrice') }}
                </span>
                <template v-slot:input>
                  <v-text-field
                    v-model="item.price"
                    :label="$t('pricelists.products.editPricesDialog.priceLabel')"
                    single-line
                    type="text"
                    inputmode="decimal"
                  ></v-text-field>
                </template>
              </v-edit-dialog>
            </template>

            <template v-slot:item.action="{ item }">
              <v-tooltip bottom :disabled="!isPackagingUsedInOtherPricelists(item.id)">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      icon
                      x-small
                      :color="isPackagingUsedInOtherPricelists(item.id) ? 'grey' : 'error'"
                      :disabled="isPackagingUsedInOtherPricelists(item.id)"
                      @click="onDeletePackaging(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>{{ $t('pricelists.products.editPricesDialog.usedInOtherPricelists') }}</span>
              </v-tooltip>
            </template>

            <template v-slot:footer>
              <v-card-actions class="pt-0">
                <v-spacer></v-spacer>
                <v-btn
                  color="secondary"
                  text
                  small
                  @click="onAddPackaging"
                >
                  {{ $t('pricelists.products.editPricesDialog.addPackaging') }}
                </v-btn>
              </v-card-actions>
            </template>
          </v-data-table>

          <v-alert
            v-else
            type="warning"
            outlined
          >
            {{ $t('pricelists.products.editPricesDialog.noPrices') }}
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="onCancel"
            :disabled="saving"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="onSave"
            :loading="saving"
            :disabled="!hasChanges"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getLocalizedProductName, getLocalizedPriceRangeName } from '@/utils/i18n-helpers';

export default {
  name: 'EditPriceRangePricesDialog',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: null,
    },
    priceRangeIndex: {
      type: Number,
      default: null,
    },
    pricelistId: {
      type: String,
      default: null,
    },
  },

  data: () => ({
    editablePrices: [],
    originalPrices: [],
    saving: false,
    deleteError: false,
    deleteErrorMessage: '',
  }),

  computed: {
    productNameLocalized() {
      return this.product ? getLocalizedProductName(this.product, this.userLanguage) : '';
    },
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    userLanguage() {
      return this.$i18n.locale || 'it';
    },

    priceRangeName() {
      if (!this.product || this.priceRangeIndex === null) return '';
      const priceRange = this.product.priceranges?.[this.priceRangeIndex];
      if (!priceRange) return '';
      return getLocalizedPriceRangeName(priceRange, this.userLanguage) || '';
    },

    productUm() {
      return this.product?.um || 'l';
    },

    priceHeaders() {
      return [
        {
          text: this.$t('pricelists.products.editPricesDialog.headers.packaging'),
          value: 'packaging',
          sortable: false,
          width: '40%',
        },
        {
          text: this.$t('pricelists.products.editPricesDialog.headers.price'),
          value: 'price',
          sortable: false,
          align: 'end',
        },
        {
          text: '',
          value: 'action',
          sortable: false,
          width: '80px',
          align: 'center',
        },
      ];
    },

    hasChanges() {
      // Se la fascia era inizialmente vuota, considera sempre come modificato per permettere il salvataggio
      if (this.originalPrices.length === 0 && this.editablePrices.length > 0) {
        // Verifica se c'è almeno un elemento con packaging o prezzo compilato
        return this.editablePrices.some((p) => {
          const hasPackaging = p.packaging && p.packaging.trim() !== '';
          const hasPrice = p.price !== null && p.price !== undefined && p.price > 0;
          return hasPackaging || hasPrice;
        });
      }
      return JSON.stringify(this.editablePrices) !== JSON.stringify(this.originalPrices);
    },
  },

  watch: {
    value(newValue) {
      if (newValue === true) {
        this.initPrices();
      }
    },
  },

  methods: {
    parseDecimal(value) {
      if (value === null || value === undefined || value === '') return null;
      const raw = `${value}`.trim();
      // Mantieni solo cifre, separatori decimali ("," ".") e segno "-"
      let s = raw.replace(/[^\d,.-]/g, '');

      // Identifica i separatori presenti
      const hasComma = s.includes(',');
      const hasDot = s.includes('.');

      if (hasComma && hasDot) {
        // Usa l'ultimo separatore come separatore decimale
        const lastComma = s.lastIndexOf(',');
        const lastDot = s.lastIndexOf('.');
        const decimalSep = lastComma > lastDot ? ',' : '.';
        const thousandSep = decimalSep === ',' ? '.' : ',';

        // Rimuovi i separatori delle migliaia e sostituisci il separatore decimale con "."
        s = s.split(thousandSep).join('');
        s = s.replace(decimalSep, '.');
      } else if (hasComma) {
        // Tratta "," come separatore decimale e rimuovi eventuali punti come migliaia
        s = s.split('.').join('');
        s = s.replace(',', '.');
      } else if (hasDot) {
        // Tratta "." come separatore decimale e rimuovi eventuali virgole come migliaia
        s = s.split(',').join('');
        // s già con "." come decimale
      }

      const parsed = parseFloat(s);
      return !Number.isNaN(parsed) ? parsed : null;
    },

    // Normalizza il packaging come stringa per mantenere compatibilità con mobile
    normalizePackagingString(value) {
      if (value === null || value === undefined || value === '') return '';
      const raw = `${value}`.trim();
      // Mantieni solo cifre, separatori decimali ("," ".") e segno "-"
      let s = raw.replace(/[^\d,.-]/g, '');
      // Sostituisci punto con virgola per compatibilità con mobile
      s = s.replace('.', ',');
      return s;
    },

    initPrices() {
      if (!this.product || this.priceRangeIndex === null || !this.pricelistId) {
        this.editablePrices = [];
        this.originalPrices = [];
        return;
      }

      const priceRange = this.product.priceranges?.[this.priceRangeIndex];

      // Se la fascia non ha prezzi, inizializza con un elemento vuoto per permettere l'aggiunta
      if (!priceRange || !priceRange.prices || priceRange.prices.length === 0) {
        this.editablePrices = [{
          id: this.generateId(),
          packaging: '',
          price: null,
        }];
        this.originalPrices = [];
        return;
      }

      // Crea copia dei prezzi per editing
      this.editablePrices = priceRange.prices.map((p) => ({
        id: p.id,
        packaging: this.normalizePackagingString(p.packaging),
        price: this.cleanPrice(p[this.pricelistId]),
      }));

      // Salva copia originale per confronto
      this.originalPrices = JSON.parse(JSON.stringify(this.editablePrices));
    },

    cleanPrice(price) {
      if (price === null || price === undefined) return null;
      return this.parseDecimal(price);
    },

    formatPackaging(packaging) {
      return `${packaging} ${this.umLongPlural(this.productUm)}`;
    },

    formatPrice(price) {
      const val = this.parseDecimal(price);
      if (val === null || val === 0) {
        return '—';
      }
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
      }).format(val);
    },

    umLongPlural(um) {
      switch (um) {
        case 'kg': return this.$t('products.units.kgPlural');
        case 'l': return this.$t('products.units.lPlural');
        case 'mq': return this.$t('products.units.mqPlural');
        case 'pz': return this.$t('products.units.pzPlural');
        default: return '';
      }
    },

    onPriceSave(item) {
      // Normalizza l'input (supporta ","/".", simboli € e separatori migliaia)
      const normalizedPrice = this.parseDecimal(item.price);
      // Trova l'elemento nell'array e aggiorna il valore
      const index = this.editablePrices.findIndex((p) => p.id === item.id);
      if (index >= 0) {
        this.$set(this.editablePrices, index, { ...item, price: normalizedPrice });
      }
      console.log('Price saved:', item); // eslint-disable-line no-console
    },

    onPackagingSave(item) {
      // Normalizza l'input come stringa per compatibilità con mobile
      const normalizedPackaging = this.normalizePackagingString(item.packaging);
      // Trova l'elemento nell'array e aggiorna il valore
      const index = this.editablePrices.findIndex((p) => p.id === item.id);
      if (index >= 0) {
        this.$set(this.editablePrices, index, { ...item, packaging: normalizedPackaging });
      }
      console.log('Packaging saved:', item); // eslint-disable-line no-console
    },

    onAddPackaging() {
      const newPackaging = {
        id: this.generateId(),
        packaging: '',
        price: null,
      };
      this.editablePrices.push(newPackaging);
    },

    onDeletePackaging(item) {
      // Il controllo è già fatto dal pulsante disabilitato,
      // ma manteniamo un check di sicurezza
      if (this.isPackagingUsedInOtherPricelists(item.id)) {
        this.deleteErrorMessage = this.$t('pricelists.products.editPricesDialog.cannotDeletePackaging');
        this.deleteError = true;
        return;
      }

      const index = this.editablePrices.findIndex((p) => p.id === item.id);
      if (index >= 0) {
        this.editablePrices.splice(index, 1);
      }
    },

    isPackagingUsedInOtherPricelists(packagingId) {
      // Recupera il packaging originale dal prodotto
      const priceRange = this.product.priceranges?.[this.priceRangeIndex];
      if (!priceRange || !priceRange.prices) return false;

      const originalPackaging = priceRange.prices.find((p) => p.id === packagingId);
      if (!originalPackaging) return false;

      // Ottiene tutti i listini
      const pricelists = this.$store.getters['pricelists/docs'] || [];

      // Verifica se esistono prezzi definiti in altri listini
      // eslint-disable-next-line no-restricted-syntax
      for (const pricelist of pricelists) {
        // Salta il listino corrente
        // eslint-disable-next-line no-continue
        if (pricelist.id === this.pricelistId) continue;

        // Verifica se il prezzo per questo listino è definito e > 0
        const priceForPricelist = originalPackaging[pricelist.id];
        if (priceForPricelist !== null && priceForPricelist !== undefined && priceForPricelist > 0) {
          return true;
        }
      }

      return false;
    },

    generateId() {
      return `packaging_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },

    async onSave() {
      this.saving = true;

      try {
        // Prepara i dati aggiornati con packaging e prezzi
        // Filtra gli elementi completamente vuoti (senza packaging e senza prezzo valido)
        const updatedPrices = this.editablePrices
          .map((p) => {
            const packaging = this.normalizePackagingString(p.packaging);
            const price = this.parseDecimal(p.price);

            return {
              id: p.id,
              // Mantieni packaging come stringa per compatibilità con mobile
              packaging,
              price: price || 0,
            };
          })
          .filter((p) => {
            // Mantieni solo gli elementi che hanno almeno packaging o prezzo valido
            const hasPackaging = p.packaging && p.packaging.trim() !== '';
            const hasPrice = p.price !== null && p.price !== undefined && p.price > 0;
            return hasPackaging || hasPrice;
          });

        // Emetti evento con i prezzi aggiornati
        this.$emit('save', {
          productId: this.product.id,
          priceRangeIndex: this.priceRangeIndex,
          pricelistId: this.pricelistId,
          prices: updatedPrices,
        });

        this.dialog = false;
      } catch (error) {
        console.error('Error saving prices:', error); // eslint-disable-line no-console
      } finally {
        this.saving = false;
      }
    },

    onCancel() {
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
.price-table {
  background-color: #fffff0;
}
</style>
